import type { ChartContext } from '../contexts/ChartContext'

export const getTypeFromSubtype = (
  subtype: IndicatorSubtype
): IndicatorType | undefined =>
  ['sma', 'base100', 'variation'].includes(subtype)
    ? 'series'
    : ['msd'].includes(subtype)
    ? 'plotline'
    : ['minmax'].includes(subtype)
    ? 'label'
    : undefined

export const addMinMaxLabels = (
  curSeries: Highcharts.SeriesOptionsType[],
  indicatorId: string,
  seriesId: string
) =>
  curSeries.map((s) => {
    if (s.id !== seriesId) {
      return s
    }
    return {
      ...s,
      dataLabels: [
        {
          id: indicatorId,
          enabled: true,
          formatter: function () {
            const series = this.series as Highcharts.Series
            const xAxis = series.xAxis
            return this.y === series.dataMin ||
              this.y === series.dataMax ||
              this.x === xAxis.getExtremes().dataMax
              ? formatThousands(this.y)
              : null
          } as Highcharts.DataLabelsFormatterCallbackFunction,
          style: { fontSize: '15px' },
        },
      ],
    } as Highcharts.SeriesLineOptions | Highcharts.SeriesColumnOptions
  })

export const addSavedIndicator = (
  indicator: Indicator,
  context: Pick<ChartContext, 'addPlotLine' | 'setSeries'>
) => {
  const { addPlotLine, setSeries } = context

  if (indicator.type === 'plotline') {
    const step = indicator.steps[indicator.steps.length - 1]
    if (step.subtype === 'msd') {
      // Delay addition of plotline in case target series is an indicator
      setTimeout(() =>
        addPlotLine({
          id: indicator.id,
          name: indicator.name,
          targetId: indicator.targetId,
          type: step.subtype,
          options: step.options,
        })
      )
    } else {
      throw new Error(`Invalid plotline subtype: ${step.subtype}`)
    }
  } else if (indicator.type === 'label') {
    const subtype = indicator.steps[indicator.steps.length - 1].subtype
    if (subtype === 'minmax') {
      setSeries((curSeries) =>
        addMinMaxLabels(curSeries, indicator.id, indicator.targetId)
      )
    } else {
      throw new Error(`Invalid label subtype: ${subtype}`)
    }
  }
}

const formatThousands = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return null
  }

  const numericSymbols = ['k', 'M', 'B', 'T']

  for (let i = numericSymbols.length - 1; i >= 0; i--) {
    const thousand = Math.pow(10, 3 * (i + 1))
    if (Math.abs(value) >= thousand) {
      return `${(value / thousand).toFixed(1)} ${numericSymbols[i]}`
    }
  }

  return value.toFixed(1)
}
